import FileInput from '@amzn/meridian/file-input';
import Papa from 'papaparse';
import React from 'react';
import { MacroAsinFacingData, AsinFacingData } from '../types/zoneMapType';
import { downloadCSV } from '../utils/CsvUtils';

interface ZoneData {
  zoneName: string;
  zoneId: string;
  productTypes: string;
  isAgile: boolean;
  fixtureIds: string[];
}

interface LdpTranslationButtonProps {
  fixtureIdToZoneDataMap: Record<
    string,
    {
      zoneName: string;
      zoneId: string;
      productTypes: string;
      isAgile: boolean;
    }
  >;
}

const LdpTranslationButton = ({ fixtureIdToZoneDataMap }: LdpTranslationButtonProps) => {
  const agileFixtureIdToZoneDataMap = new Map(
    Object.entries(fixtureIdToZoneDataMap).filter(([, data]) => data.isAgile),
  );

  const aggregateZoneData = (): ZoneData[] => {
    const grouped = Array.from(agileFixtureIdToZoneDataMap).reduce<Record<string, ZoneData>>(
      (acc, [key, { zoneName, zoneId, productTypes, isAgile }]) => {
        const existing = acc[zoneId] || {
          zoneName,
          zoneId,
          productTypes,
          isAgile,
          fixtureIds: [],
        };
        return {
          ...acc,
          [zoneId]: {
            ...existing,
            fixtureIds: [...existing.fixtureIds, key],
          },
        };
      },
      {},
    );

    return Object.values(grouped);
  };

  const agileZoneData = aggregateZoneData();
  const parseLdpData = (dataArray: { [key: string]: string }[]) => {
    const agileFixtureLists: Set<string> = new Set(agileFixtureIdToZoneDataMap.keys());
    return dataArray
      .map((row) => ({
        asin: row.ASIN,
        facings: parseInt(row['Faceout Quantity'], 10),
        fixtureId: row['Rack ID'],
        storeId: row['Store ID'],
      }))
      .filter((row: AsinFacingData) => agileFixtureLists.has(row.fixtureId) && row.asin.length > 0);
  };

  const aggregateDataByZone = (ldpData: AsinFacingData[], zoneDataList: ZoneData[]): MacroAsinFacingData[] =>
    zoneDataList.flatMap((zone) => {
      const asinTotalFacing: { [asin: string]: number } = {};
      ldpData.forEach((asinFacingData) => {
        if (zone.fixtureIds?.includes(asinFacingData.fixtureId)) {
          asinTotalFacing[asinFacingData.asin] = (asinTotalFacing[asinFacingData.asin] ?? 0) + asinFacingData.facings;
        }
      });

      return Object.entries(asinTotalFacing).map(([asin, facings]: [string, number]) => ({
        zoneName: zone.zoneName,
        fixtureIds: zone.fixtureIds,
        asin,
        facings,
      }));
    });

  const onFileAttached = (files: File[] = []): void => {
    const file: File | undefined = files[0];
    if (file) {
      const reader: FileReader = new FileReader();
      reader.onload = (event: ProgressEvent<FileReader>): void => {
        if (event.target && event.target.result) {
          const csvContent = Papa.parse(event.target.result as string, {
            header: true,
          });
          const dataArray = csvContent.data as { [key: string]: string }[];
          const matchedData = parseLdpData(dataArray);
          const exportData = aggregateDataByZone(matchedData, agileZoneData ?? []);
          downloadCSV(exportData, 'AsinsByZone');
        }
      };
      reader.readAsText(file);
    } else {
      console.error('No file attached.');
    }
  };
  return (
    <FileInput
      accept=".csv"
      uploadButtonSize="small"
      uploadButtonLabel="Translate LDP Plan"
      onFileAttached={onFileAttached}
      uploadButtonDisabled={agileZoneData.length === 0}
      data-testid="file-input"
      uploadButtonType="primary"
    />
  );
};

export default LdpTranslationButton;
